import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

export interface AuthState {
  // State
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionExpired: boolean;

  // Actions
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setSessionExpired: (expired: boolean) => void;
  signOut: () => void;
  reset: () => void;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  sessionExpired: false,
};

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      ...initialState,
      // Start as loading so ProtectedRoute waits for session restore before
      // making an auth decision. signOut/reset use initialState which has
      // isLoading: false, so they correctly clear the loading state.
      isLoading: true,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          error: null,
        }),

      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      clearError: () =>
        set({
          error: null,
        }),

      setSessionExpired: (expired) =>
        set({
          sessionExpired: expired,
        }),

      signOut: () =>
        set({
          ...initialState,
        }),

      reset: () =>
        set({
          ...initialState,
        }),
    }),
    { name: 'auth-store' }
  )
);
